"use client";

import React from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectItem } from "@/components/ui/simple-select";
import { Hash } from "lucide-react";
import type { SimpleFieldConfig } from "../../../types/index";

interface NumberFilterInputProps {
  fieldId: string;
  fieldConfig: SimpleFieldConfig;
  currentFilter?: { type: string; value: any };
  addAdvancedFilter: (fieldId: string, filterType: string, value: any) => void;
}

export function NumberFilterInput({
  fieldId,
  fieldConfig,
  currentFilter,
  addAdvancedFilter,
}: NumberFilterInputProps) {
  const filterType = currentFilter?.type || "numberRange";
  const isRange = filterType === "numberRange";

  return (
    <div className="space-y-2">
      <Select
        value={filterType}
        onValueChange={(type) => {
          if (type === "numberRange") {
            addAdvancedFilter(fieldId, type, currentFilter?.value || {});
          } else {
            const val = currentFilter?.value;
            const num = typeof val === "object" ? val?.min ?? val?.max ?? "" : val;
            addAdvancedFilter(fieldId, type, num === "" ? undefined : Number(num));
          }
        }}
        className="w-full"
      >
        <SelectItem value="numberRange">Range (min–max)</SelectItem>
        <SelectItem value="eq">Equals (eq)</SelectItem>
        <SelectItem value="neq">Not Equal (neq)</SelectItem>
        <SelectItem value="gt">Greater Than (gt)</SelectItem>
        <SelectItem value="gte">Greater or Equal (gte)</SelectItem>
        <SelectItem value="lt">Less Than (lt)</SelectItem>
        <SelectItem value="lte">Less or Equal (lte)</SelectItem>
      </Select>
      {isRange ? (
        <>
          <div className="flex items-center gap-2">
            <Hash className="h-4 w-4 text-gray-400" />
            <Input
              type="number"
              placeholder="Min"
              value={currentFilter?.value?.min ?? ""}
              onChange={(e) =>
                addAdvancedFilter(fieldId, "numberRange", {
                  min: e.target.value === "" ? undefined : Number(e.target.value),
                  max: currentFilter?.value?.max,
                })
              }
              className="flex-1"
            />
          </div>
          <div className="flex items-center gap-2">
            <Hash className="h-4 w-4 text-gray-400" />
            <Input
              type="number"
              placeholder="Max"
              value={currentFilter?.value?.max ?? ""}
              onChange={(e) =>
                addAdvancedFilter(fieldId, "numberRange", {
                  min: currentFilter?.value?.min,
                  max: e.target.value === "" ? undefined : Number(e.target.value),
                })
              }
              className="flex-1"
            />
          </div>
        </>
      ) : (
        <div className="flex items-center gap-2">
          <Hash className="h-4 w-4 text-gray-400" />
          <Input
            type="number"
            placeholder={`Value for ${filterType}`}
            value={
              typeof currentFilter?.value === "number"
                ? currentFilter.value
                : currentFilter?.value?.min ?? currentFilter?.value?.max ?? ""
            }
            onChange={(e) => {
              const val = e.target.value === "" ? undefined : Number(e.target.value);
              addAdvancedFilter(fieldId, filterType, val);
            }}
            className="flex-1"
          />
        </div>
      )}
    </div>
  );
}
